import Color from 'color';
import {FastAverageColor} from 'fast-average-color';
import type {RefObject} from 'react';
import {useEffect, useRef} from 'react';

/*
  This hook is used to return the dominant color of an image,
  we are using a setInterval() to ensure that the image is loaded,
  we had a flakyness on Safari where the image was not loaded and
  the color returned was black.
*/

const getDominantImageColor = (img: HTMLImageElement): string => {
  const fac = new FastAverageColor();
  const dominantColor = fac.getColor(img);
  const color = Color(dominantColor.hex);
  const colorLuminosity = color.luminosity();
  if (colorLuminosity <= 0.5) {
    return dominantColor.hex;
  }
  // 0.4 is arbitrary fixed, it seems the best choice on an UX perspective.
  const darkenIntensity = Math.round(colorLuminosity * 10) / 10 - 0.4;
  const darkenizedColor = color.darken(darkenIntensity);
  return darkenizedColor.hex();
};

const MAX_DURATION = 2000; // 2 seconds.
const INTERVAL_DURATION = 20;

const useImageDominantColor = (
  onColorAvailable: (color: string) => void
): RefObject<HTMLImageElement> => {
  const img = useRef<HTMLImageElement>(null);

  useEffect(() => {
    let counter = 0;
    const interval = setInterval(() => {
      counter += INTERVAL_DURATION;

      if (img.current !== null && img.current.complete) {
        const color = getDominantImageColor(img.current);
        onColorAvailable(color);
        clearInterval(interval);
      }

      if (counter >= MAX_DURATION) {
        clearInterval(interval);
      }
    }, INTERVAL_DURATION);
  }, [onColorAvailable]);

  return img;
};

export default useImageDominantColor;
